import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, PoundSterling } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import { money } from "@/app/pages/commissions/CommissionList";

/** Commission card for the partner application detail page: shows the linked
 * commission record (if any) or offers evaluation once an outcome is recorded. */
export function ApplicationCommissionCard({ applicationId }) {
  const { user } = useAuth();
  const base = user.role === "admin" ? "/app/admin" : "/app/partner";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/applications/${applicationId}/commission`)
    .then((r) => setData(r.data)).catch(() => setData({ commission: null, evaluatable: false })), [applicationId]);
  useEffect(() => { load(); }, [load]);

  const evaluate = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${applicationId}/commission/evaluate`);
      toast.success("Potential commission created.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const commission = data.commission;

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="application-commission">
      <h2 className="font-display text-xl font-bold text-ink">Commission</h2>
      {commission ? (
        <div className="mt-3">
          <div className="flex items-center justify-between gap-3">
            <span className="rounded-full bg-cobalt/10 px-3 py-1 text-xs font-bold capitalize text-cobalt" data-testid="application-commission-status">{commission.status}</span>
            <span className="text-sm font-bold text-ink" data-testid="application-commission-total">{money(commission.total, commission.currency)}</span>
          </div>
          <p className="mt-2 text-xs text-slatey">{commission.rule_label} · config v{commission.config_version}</p>
          <Link to={`${base}/commissions/${commission.id}`} data-testid="application-commission-link"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-ink hover:border-cobalt hover:text-cobalt">
            <PoundSterling className="h-3.5 w-3.5" /> View commission record
          </Link>
        </div>
      ) : data.evaluatable ? (
        <div className="mt-3">
          <p className="text-xs text-slatey">A successful outcome is recorded. Evaluate to create a potential commission — application success alone never means payable.</p>
          <button onClick={evaluate} disabled={busy} data-testid="commission-evaluate"
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Evaluate commission
          </button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-slatey" data-testid="commission-not-evaluatable">Commission becomes available once a successful outcome (offer / accepted) is recorded.</p>
      )}
    </section>
  );
}
